import {
    MouseEvent, useCallback, useEffect, useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { Column } from 'react-rainbow-components';
import { TableProps } from 'react-rainbow-components/components/Table';
import useWorkplaces from 'data/firestore/agent/workplace/useCollectionWithPagination';
import { orderBy, OrderByDirection, query } from 'firebase/firestore';
import { EntityGet } from 'data/firestore/types';
import { Workplace } from 'data/firestore/agent/workplace/types';
import useAlgoliaSearchData from 'data/algolia/useSearchData';
import Pagination from 'components/Pagination';
import DateTimeColumn from 'components/DateTimeColumn';
import Table from 'components/Table';
import useCustomSearchParams from 'hooks/useCustomSearchParams';
import ColumnTags from 'components/TableColumns/Tags';
import { ALGOLIA_INDEX_WORKPLACES, PAGE_SIZE } from '../../../constants';
import {
    Container, EntriesText, PaginationContainer,
} from './styled';
import UserCreatedByColumn from './columns/createdBy';
import WorkplaceColumn from './columns/workplace';
import ActionsColumn from './columns/actions';
import buildAlgoliaFilters from './helpers/buildAlgoliaFilters';

interface WorkplacesListProps {
    filterValues: Record<string, string | string[] | null>;
}

const WorkplacesList = ({ filterValues }: WorkplacesListProps) => {
    const { agentId = '' } = useParams<{ agentId: string }>();
    const filters = useMemo(() => Object.values(filterValues).filter((value) => !!value).join('|'), [filterValues]);

    const [searchParams, setSearchParams] = useCustomSearchParams({
        page: {
            type: 'number',
            default: 1,
        },
        sortDirection: {
            type: 'string',
            default: 'desc',
        },
    });

    const { page: activePage, sortDirection = 'desc' } = searchParams;

    const useAlgoliaSearch = Boolean(filters || activePage > 1);

    const {
        data: workplaces,
        isLoading: isLoadingWorkplaces,
        totalRecords: totalWorkplaces,
        error,
    } = useWorkplaces(
        agentId,
        {
            disabled: !agentId || useAlgoliaSearch,
            limit: PAGE_SIZE,
            listQueryFn: (ref) => query(ref, orderBy('createdAt', sortDirection as OrderByDirection)),
            track: [agentId, sortDirection],
        },
    );

    const algoliaData = useAlgoliaSearchData<EntityGet<Workplace>>({
        search: (filterValues.search as string) ?? '',
        activePage,
        pageSize: PAGE_SIZE,
        indexName: ALGOLIA_INDEX_WORKPLACES,
        filters: buildAlgoliaFilters({ filters: filterValues, agentId }),
        enabled: useAlgoliaSearch,
    });

    const disableSorting = useMemo(
        () => Boolean(algoliaData?.hits),
        [algoliaData?.hits],
    );

    const dataToShow = algoliaData?.hits || workplaces;
    const totalHits = typeof algoliaData?.nbHits === 'number' ? algoliaData.nbHits : totalWorkplaces;
    const pages = Math.ceil(totalHits / PAGE_SIZE);
    const firstItemOfActivePage = dataToShow.length === 0 ? 0 : PAGE_SIZE * (activePage - 1) + 1;
    const lastItemOfActivePage = Math.min(
        PAGE_SIZE * activePage,
        (PAGE_SIZE * activePage - PAGE_SIZE) + dataToShow.length,
    );

    const handlePageChange = (event: React.ChangeEvent<unknown>, pageNumber: number) => {
        if (isLoadingWorkplaces) return;
        setSearchParams({ page: pageNumber });
    };

    useEffect(() => {
        const totalPages = algoliaData?.nbPages;
        if (algoliaData.isLoading) return;
        if (totalPages === 0) {
            setSearchParams({ page: undefined }, true);
        }
        if (
            totalPages !== undefined
                && totalPages > 0
                && activePage > totalPages
        ) {
            setSearchParams({ page: 1 }, true);
        }
    }, [
        algoliaData?.nbPages,
        activePage,
        setSearchParams,
        algoliaData.isLoading,
    ]);

    const handleOnSort = useCallback(
        (
            event: MouseEvent<HTMLElement>,
            field: string,
            nextSortDirection: 'asc' | 'desc',
        ) => {
            if (disableSorting) return;
            setSearchParams({ sortDirection: nextSortDirection });
        },
        [disableSorting, setSearchParams],
    );

    const isLoading = isLoadingWorkplaces || algoliaData.isLoading;

    if (error || (!workplaces && !isLoading)) {
        return (
            <Container>
                <h1>Something went wrong</h1>
                {error?.message && (
                    <p>
                        {error.name}
                        :
                        {' '}
                        {error.code}
                    </p>
                )}
            </Container>
        );
    }

    return (
        <Container>
            <Table
                data={dataToShow}
                keyField="id"
                isLoading={isLoading}
                sortedBy="createdAt"
                onSort={handleOnSort}
                sortDirection={sortDirection as TableProps['sortDirection']}
            >
                <Column
                    header="Workplace"
                    field="id"
                    component={WorkplaceColumn}
                />
                <Column
                    header="Tags"
                    field="tags"
                    component={ColumnTags}
                    maxBadges={1}
                    agentId={agentId}
                    entity="workspace"
                />
                <Column
                    header="Created At"
                    field="createdAt"
                    component={DateTimeColumn}
                    sortable={!disableSorting}
                    width={200}
                />
                <Column
                    header="Created By"
                    field="createdBy"
                    component={UserCreatedByColumn}
                    width={300}
                    sortable={!disableSorting}
                />
                <Column
                    field="id"
                    component={ActionsColumn}
                    width={100}
                    agentId={agentId}
                />
            </Table>
            <PaginationContainer>
                <EntriesText>
                    Showing
                    {' '}
                    {firstItemOfActivePage}
                    {' '}
                    to
                    {' '}
                    {lastItemOfActivePage}
                    {' '}
                    of
                    {' '}
                    {totalHits}
                    {' '}
                    entries.
                </EntriesText>
                <Pagination
                    activePage={activePage}
                    pages={pages}
                    onChange={handlePageChange}
                />
            </PaginationContainer>
        </Container>
    );
};

export default WorkplacesList;
